import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook customizado para gerenciar a persistência de PIX pendentes
 * Permite que o usuário feche e reabra o modal sem perder um PIX gerado
 */
export const usePendingPix = (parentId, companyId) => {
  const [pendingPix, setPendingPix] = useState(null);
  const isInitialized = useRef(false);
  
  // Gerar chave única para o localStorage baseada no parent e company
  const getStorageKey = useCallback(() => {
    return `pending_pix_${parentId}_${companyId}`;
  }, [parentId, companyId]);
  
  // Carregar PIX pendente do localStorage na inicialização (apenas uma vez)
  useEffect(() => {
    if (!parentId || !companyId || isInitialized.current) return;
    
    isInitialized.current = true;
    
    try {
      const storageKey = `pending_pix_${parentId}_${companyId}`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const pixData = JSON.parse(stored);
        
        // Verificar se o PIX não expirou (10 minutos)
        const createdAt = new Date(pixData.createdAt);
        const now = new Date();
        const diffMinutes = (now - createdAt) / (1000 * 60);
        
        if (diffMinutes < 10) {
          console.log('🔄 PIX pendente encontrado no localStorage:', pixData);
          setPendingPix(pixData);
        } else {
          console.log('⏰ PIX pendente expirado, removendo do localStorage');
          localStorage.removeItem(storageKey);
          setPendingPix(null);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar PIX pendente do localStorage:', error);
      // Se houver erro, limpar o localStorage
      try {
        const storageKey = `pending_pix_${parentId}_${companyId}`;
        localStorage.removeItem(storageKey);
      } catch (e) {
        console.error('❌ Erro ao limpar localStorage corrompido:', e);
      }
    }
  }, [parentId, companyId]); // Removido getStorageKey das dependências

  // Salvar PIX pendente no localStorage
  const savePendingPix = useCallback((pixData, studentId, amount) => {
    if (!parentId || !companyId) return;
    
    try {
      const storageKey = getStorageKey();
      const dataToStore = {
        ...pixData,
        studentId,
        amount,
        createdAt: new Date().toISOString(),
        parentId,
        companyId
      };
      
      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      setPendingPix(dataToStore);
      
      console.log('💾 PIX pendente salvo no localStorage:', dataToStore);
    } catch (error) {
      console.error('❌ Erro ao salvar PIX pendente no localStorage:', error);
    }
  }, [parentId, companyId, getStorageKey]);

  // Remover PIX pendente do localStorage
  const clearPendingPix = useCallback(() => {
    if (!parentId || !companyId) return;
    
    try {
      const storageKey = getStorageKey();
      localStorage.removeItem(storageKey);
      setPendingPix(null);
      
      console.log('🗑️ PIX pendente removido do localStorage');
    } catch (error) {
      console.error('❌ Erro ao remover PIX pendente do localStorage:', error);
    }
  }, [parentId, companyId, getStorageKey]);

  // Verificar se existe PIX pendente para um estudante específico
  const hasPendingPixForStudent = useCallback((studentId) => {
    return pendingPix && pendingPix.studentId === studentId;
  }, [pendingPix]);

  // Obter PIX pendente para um estudante específico
  const getPendingPixForStudent = useCallback((studentId) => {
    if (hasPendingPixForStudent(studentId)) {
      return pendingPix;
    }
    return null;
  }, [pendingPix, hasPendingPixForStudent]);

  // Verificar se o PIX pendente ainda é válido (não expirou)
  const isPendingPixValid = useCallback(() => {
    if (!pendingPix) return false;
    
    const createdAt = new Date(pendingPix.createdAt);
    const now = new Date();
    const diffMinutes = (now - createdAt) / (1000 * 60);
    
    return diffMinutes < 10; // PIX válido por 10 minutos
  }, [pendingPix]);

  return {
    pendingPix,
    savePendingPix,
    clearPendingPix,
    hasPendingPixForStudent,
    getPendingPixForStudent,
    isPendingPixValid
  };
};
